/**
 * FollowUpTemplateSelector Component - Template selection and generation for follow-ups
 */

import React, { useState } from "react";
import {
  Modal,
  Select,
  Button,
  Input,
  Space,
  message,
  Spin,
  Card,
  Typography,
} from "antd";
import {
  MessageOutlined,
  CopyOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { getTemplates, generateTemplate } from "../services/templateService.js";

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

/**
 * FollowUpTemplateSelector Component
 * @param {Object} props
 * @param {Object} props.followUp - Follow-up object
 * @param {boolean} props.visible - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 */
const FollowUpTemplateSelector = ({ followUp, visible, onClose }) => {
  const [templateType, setTemplateType] = useState(null);
  const [template, setTemplate] = useState("");
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [copied, setCopied] = useState(false);

  // Load available templates when modal opens
  React.useEffect(() => {
    if (visible && followUp) {
      loadTemplates();
    }
  }, [visible, followUp]);

  const loadTemplates = async () => {
    if (!followUp?.entityType) return;

    try {
      const result = await getTemplates(
        followUp.entityType,
        followUp.entityContext?.status
      );
      if (result.success) {
        setTemplates(result.data || []);
      } else {
        message.error(result.error || "Failed to load templates");
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      message.error("Failed to load templates");
    }
  };

  const handleTemplateTypeChange = async (value) => {
    setTemplateType(value);
    setTemplate("");
    setCopied(false);

    if (!value || !followUp) return;

    setLoading(true);
    try {
      const result = await generateTemplate({
        followUpId: followUp.id,
        templateType: value,
        entityType: followUp.entityType,
        entityStatus: followUp.entityContext?.status,
        entityContext: followUp.entityContext,
      });

      if (result.success) {
        setTemplate(result.data || "");
      } else {
        message.error(result.error || "Failed to generate template");
        setTemplate("");
      }
    } catch (error) {
      console.error("Error generating template:", error);
      message.error("Failed to generate template");
      setTemplate("");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!template) return;

    try {
      await navigator.clipboard.writeText(template);
      setCopied(true);
      message.success("Template copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      message.error("Failed to copy to clipboard");
    }
  };

  const handleClose = () => {
    setTemplateType(null);
    setTemplate("");
    setCopied(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined />
          <span>Follow-up Template Generator</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>,
        <Button
          key="copy"
          type="primary"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={handleCopy}
          disabled={!template}
        >
          {copied ? "Copied!" : "Copy to Clipboard"}
        </Button>,
      ]}
      width={800}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Text strong>Select Template Type: </Text>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Choose a template..."
            value={templateType}
            onChange={handleTemplateTypeChange}
          >
            {templates.map((tmpl) => (
              <Option key={tmpl.value} value={tmpl.value}>
                {tmpl.label} - {tmpl.description}
              </Option>
            ))}
          </Select>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Generating template...</Text>
            </div>
          </div>
        )}

        {template && !loading && (
          <Card>
            <Title level={5}>Template Preview</Title>
            <TextArea
              value={template}
              readOnly
              rows={12}
              style={{ fontFamily: "monospace", fontSize: "13px" }}
            />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Click "Copy to Clipboard" to copy this template, then paste it
                into WhatsApp or Email.
              </Text>
            </div>
          </Card>
        )}

        {!templateType && !loading && (
          <Card>
            <Text type="secondary">
              Select a template type above to generate a follow-up message template.
            </Text>
          </Card>
        )}
      </Space>
    </Modal>
  );
};

export default FollowUpTemplateSelector;

